"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tags = void 0;
const types_1 = require("@air/types");
const querystring_1 = require("querystring");
const request_1 = require("./request");
const util_1 = require("./util");
function normalizeCreateEndpoint({ workspaceId, labels, idsToAdd, idsToRemove, clips, exclusive, }) {
    if (!(0, util_1.anyPopulatedArrays)(labels, idsToAdd, idsToRemove)) {
        return Promise.resolve({ tags: [], clips: [] });
    }
    return (0, request_1.airRequest)(types_1.EndpointType.tags, {
        body: {
            labels,
            idsToAdd,
            idsToRemove,
            clipIds: (0, util_1.normToIdListIfExists)(clips),
            exclusive,
        },
        method: 'POST',
        workspaceId,
    });
}
/** Inserts tags with the provided list of labels to the workspace, returning
 * the labels and corresponding tag ids in an array of objects
 * @param labels List of tag labels
 */
function create({ workspaceId, labels, }) {
    return normalizeCreateEndpoint({
        workspaceId,
        labels,
    });
}
/** Inserts tags with the provided list of labels to the workspace, returning
 * the labels and corresponding tag ids in an array of objects
 * Tags with no ID have their labels used to make tags.
 * @param tags Desired state of tags after this call (will do diff to not override simultaneous requests)
 * @param clip Clip to add/remove tags from
 */
function diffTagsForClip({ workspaceId, tags, clip: { id: clipId, tags: oldTags }, }) {
    // Tags that don't have IDs are labels that we want to create tags from
    const labels = (0, util_1.labelsOfUnmadeTags)(tags);
    // If an ID is present, is in tags but not oldTags we should try to add it to the clip
    const idsToAdd = (0, util_1.onlyInFormerTags)(tags, oldTags);
    // If an ID is present, is in oldTags but not tags, we should try to remove it from the clip
    const idsToRemove = (0, util_1.onlyInFormerTags)(oldTags, tags);
    return normalizeCreateEndpoint({
        workspaceId,
        clips: [clipId],
        labels,
        idsToAdd,
        idsToRemove,
    });
}
/** Adds the given tags to the given clips
 * If a tag does not have an ID, the label is used to make a new tag
 * @param tags
 * @param clips
 */
function addOrCreateTagsOnClips({ workspaceId, tags, clips, }) {
    // Tags that don't have IDs are labels that we want to create tags from
    const labels = (0, util_1.labelsOfUnmadeTags)(tags);
    const idsToAdd = (0, util_1.idsOfMadeTags)(tags);
    return normalizeCreateEndpoint({
        workspaceId,
        clips,
        labels,
        idsToAdd,
    });
}
/**
 * Updates list of tags to supplied fields
 * @param tags List of tag objects
 */
function update({ workspaceId, tags, options = { autoMerge: true }, }) {
    return (0, request_1.airRequest)(types_1.EndpointType.tags, {
        body: { tags, options },
        method: 'PATCH',
        workspaceId,
    });
}
/**
 * Merges list of tags into a new tag
 * @param tags List of tag objects
 * @param label New label for merged tags (can be an existing label to merge into)
 */
function merge({ workspaceId, tags, label, }) {
    return (0, request_1.airRequest)(types_1.EndpointType.tags, {
        body: { tags, label },
        endpoint: 'merge',
        method: 'POST',
        workspaceId,
    });
}
/**
 * Bulk remove tags from clips
 * @param idsToRemove list of tag ids to disassociate from clips
 * @param clipIds list of clipIds to perform operation on
 * @returns nothing
 */
function removeFromClips({ workspaceId, idsToRemove, clipIds, }) {
    if (!(0, util_1.allPopulatedArrays)(clipIds, idsToRemove)) {
        return Promise.resolve();
    }
    return (0, request_1.airRequest)(types_1.EndpointType.tags, {
        body: {
            idsToRemove,
            clipIds,
        },
        endpoint: 'remove',
        method: 'POST',
        workspaceId,
    });
}
/**
 * Deletes tags specified in input list
 * @param tags List of tags or tag ids to delete
 */
function del({ workspaceId, tags, }) {
    const tagIds = (0, util_1.normToIdList)(tags);
    return (0, request_1.airRequest)(types_1.EndpointType.tags, {
        body: tagIds,
        method: 'DELETE',
        workspaceId,
    });
}
/**
 * List clips with various sorts and filters
 * @param options Sort, filter, limiting, and exclude tag count options
 */
function list({ workspaceId, options = {}, }) {
    const defaults = {
        limit: 500,
        sortBy: 'recentlyUsed',
        includeTagCounts: true,
    };
    const params = Object.assign(Object.assign({}, defaults), options);
    const paramString = (0, querystring_1.stringify)(params);
    return (0, request_1.airRequest)(types_1.EndpointType.tags, {
        endpoint: `list?${paramString}`,
        workspaceId,
    });
}
exports.Tags = {
    create,
    diffTagsForClip,
    addOrCreateTagsOnClips,
    update,
    merge,
    removeFromClips,
    del,
    list,
};
//# sourceMappingURL=tags.js.map